import moment from 'moment';

import office from 'src/services/office';
import { DataTable } from 'src/services/runtime/dataTable';

import { asArray, isTestStage } from 'shared/utils/utils';
import { DATE_TIME_FORMAT_STRING } from 'shared/excel/excel';


const LogData = {
    Columns: [
        { name: 'Date', width: 50 },
        { name: 'Event Type', width: 80 },
        { name: 'Sheet Name', width: 40 },
        { name: 'Result', width: 120 },
        { name: 'Username', width: 60 },
    ],
    MaxSize: 10000,
    SheetName: 'Manual Operations Events Log',
    SheetNameTest: 'Manual Operations Log (TEST)',
    HeaderFormat: { font: { bold: true, color: '#FFFFFF', name: 'Arial' }, fill: { color: '#4285F4' } },
    BorderFormat: { 
      items: [ 'EdgeBottom', 'EdgeTop', 'EdgeRight', 'EdgeLeft'],
      // style: Excel.BorderLineStyle.dashDot, 
    },
};


export class OperationsLogger {
  get entriesCount() { return this.table?.realDataRowCount ?? 0; }

  constructor(workbook) {
    this.workbook = workbook;
    this.isEnabled = true;
    this.needFormatting = false;
    this.isOpened = false
  }

  static async open(workbook) {
    const log = new OperationsLogger(workbook);

    try {
      const worksheetName = isTestStage(STAGE) ? LogData.SheetNameTest : LogData.SheetName;
      let ws = workbook.worksheets.getItemOrNullObject(worksheetName);
      await workbook.context.sync();
      if (ws.isNullObject) {
        ws = workbook.worksheets.add(worksheetName);
      }
      
      let table = await office.getDataTable(ws.getRange('A1'));
      const headersLength = LogData.Columns.length;
      
      if (!table) {
        const range = ws.getRangeByIndexes(0, 0, 2, headersLength);
        table = await DataTable.fromRange(range);
      }
      else {
        table.fitColumnsToSize(headersLength);
      }
      
      log.table = table;
      log.isOpened = true;
    }
    catch (error) {
      console.log('Unable to open Operations Log. Error: ', error.message);
      return null;
    }

    return log;
  }

  setEnabled(value) { this.isEnabled = value; }

  log(message, eventType, sheetName, username) {
    if (!this.isOpened || !this.isEnabled) { return null; }

    const messages = asArray(message);
    const data = messages.map(msg => [moment().format(DATE_TIME_FORMAT_STRING), eventType, sheetName, msg, username]);
    return this.table.addRows(data);
  }

  getRange(index, count) {
    return this.isOpened && this.isEnabled
      ? this.table.getRows(index, count)
      : null;
  }

  async close() {
    if (!this.isOpened) { return; }

    try {
      if (this.entriesCount) {
        LogData.Columns.forEach((x, i) => {
          const width = x.width * 3;
          this.table.range.getColumn(i).format.columnWidth = width;
        });

        this.table.headerRowRange.format.set(LogData.HeaderFormat);
        this.table.setHeaders(LogData.Columns.map(x => x.name));
      }

      const toRemove = this.entriesCount - LogData.MaxSize;
      if (toRemove > 0) {
        this.table.deleteRows(0, toRemove);
      }

      await this.table.sync();
    }
    catch (error) {
      console.log('Unable to properly close Operations Log. Error: ', error.message);
    }
  }
}


export class InstanceLogger {
  static Severity = {
    Info: 'info',
    Warn: 'warn',
    Error: 'error',
  };

  entriesCount = 0;
  startIndex = undefined;
  isEnabled = true;

  constructor(globalLogger, params, parentLogger) {
    this.globalLogger = globalLogger;
    this.parentLogger = parentLogger;
    this.logParams = params;
  }

  setEnabled(isEnabled) { this.isEnabled = isEnabled; }
  setLogParams(params) {
    Object.assign(this.logParams, params);
  }

  log(message, severity = InstanceLogger.Severity.Info) {
    if (!this.globalLogger || !this.isEnabled) {
      return null;
    }

    const { userName, worksheetName, name } = this.logParams;
    const range = this.globalLogger.log(message, name, worksheetName, userName);
    if (!range) {
      return null; // logger is disabled
    }

    const count = Array.isArray(message) ? message.length : 1;
    this.updateEntriesCount(count);
    
    this.colorize(range, severity);
    return range;
  }

  info(message) {
    return this.log(message, InstanceLogger.Severity.Info);
  }

  warn(message) {
    return this.log(message, InstanceLogger.Severity.Warn);
  }

  error(message) {
    return this.log(message, InstanceLogger.Severity.Error);
  }

  updateEntriesCount(count) {
    if (this.startIndex === undefined) {
      this.startIndex = this.globalLogger.entriesCount - count; // Because is called after entries are added to log
      this.entriesCount = 0;
    }

    this.entriesCount += count;
    this.parentLogger?.updateEntriesCount(count);
  }

  close() {
    const range = this.getRange();
    if (range) {
      LogData.BorderFormat.items.forEach(side => 
        range.format.borders.getItem(side).set(this.logParams.borderFormat)
      );
    }
    return range;
  }

  getRange() {
    return this.startIndex !== undefined
      ? this.globalLogger?.getRange(this.startIndex, this.entriesCount)
      : null;
  }

  colorize(range, severity) {
    const t = {
      [InstanceLogger.Severity.Error]: '#fde7e9',
      [InstanceLogger.Severity.Warn]: '#fff4ce',
    };

    const color = t[severity] ?? '#FFFFFF';
    if (range) {
      range.format.set({ fill: { color }});
    }
  }
}


export default OperationsLogger;